import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GridOnRoundedIcon from "@mui/icons-material/GridOnRounded";
import TextSnippetRoundedIcon from "@mui/icons-material/TextSnippetRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { DASHBOARD_COLORS as C, focusRing } from "../DashboardCard";
import { EXPORT_FORMATS } from "./reportExport";
import { REPORTS } from "./reportConfig";

const FORMAT_ICONS = { xlsx: GridOnRoundedIcon, csv: TextSnippetRoundedIcon, pdf: PictureAsPdfRoundedIcon, print: PrintRoundedIcon };

const sectionLabel = { fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".04em", mb: 0.75 };

const optionSx = (selected) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  m: 0,
  p: 1,
  minWidth: 0,
  borderRadius: "10px",
  cursor: "pointer",
  border: `1px solid ${selected ? C.primary : C.border}`,
  bgcolor: selected ? C.primarySoft : "#fff",
  "&:hover": { borderColor: selected ? C.primary : "#BFDBFE" },
  "& .MuiRadio-root.Mui-focusVisible": { outline: `2px solid ${C.primary}`, outlineOffset: 1, borderRadius: "50%" },
});

function Option({ value, selected, disabled, title, detail, icon: Icon, name }) {
  return (
    <Box component="label" sx={{ ...optionSx(selected), opacity: disabled ? 0.55 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      <Radio value={value} size="small" disabled={disabled} inputProps={{ name, "aria-describedby": `${name}-${value}-detail` }} sx={{ p: 0.25 }} />
      {Icon && <Icon sx={{ fontSize: 20, color: selected ? C.primary : C.muted, flexShrink: 0 }} />}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: C.navy, lineHeight: 1.3 }}>{title}</Typography>
        <Typography id={`${name}-${value}-detail`} sx={{ fontSize: 12, color: C.muted, lineHeight: 1.35 }}>
          {detail}
        </Typography>
      </Box>
    </Box>
  );
}

// Asks for format and record scope, then runs onExport(format, scope) with progress and error feedback.
export default function ExportReportDialog({ open, onClose, tab, scopeCounts, hasQuery, isPdfBlocked, onExport, fullScreen }) {
  const [format, setFormat] = useState("xlsx");
  const [scope, setScope] = useState("filtered");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setScope("filtered");
      setError("");
    }
  }, [open]);

  const report = REPORTS[tab];
  const count = scopeCounts[scope] ?? 0;
  const pdfBlocked = format === "pdf" && isPdfBlocked?.(scope);
  const canExport = !busy && count > 0 && !pdfBlocked;

  const scopes = [
    { value: "page", title: "Current page", detail: `${scopeCounts.page} record${scopeCounts.page === 1 ? "" : "s"} visible in the table` },
    {
      value: "filtered",
      title: "All matching records",
      detail: `${scopeCounts.filtered} record${scopeCounts.filtered === 1 ? "" : "s"} ${hasQuery ? "matching the search and filters" : "(no search or filters applied)"}`,
    },
    { value: "all", title: `All ${report.title} records`, detail: `${scopeCounts.all} record${scopeCounts.all === 1 ? "" : "s"}, ignoring search and filters` },
  ];

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onExport(format, scope);
    } catch (err) {
      setError(err?.message || "The export could not be generated. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => !busy && onClose();

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      aria-labelledby="export-report-title"
      slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : "14px" } } }}
    >
      <DialogTitle id="export-report-title" sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, pb: 1 }}>
        <Box>
          <Typography component="span" sx={{ display: "block", fontWeight: 700, fontSize: 17, color: C.navy }}>
            Export report
          </Typography>
          <Typography component="span" sx={{ display: "block", fontSize: 13, color: C.muted }}>
            Category:{" "}
            <Box component="strong" sx={{ color: C.navy }}>
              {report.title}
            </Box>
          </Typography>
        </Box>
        <IconButton onClick={close} disabled={busy} size="small" aria-label="Close export" sx={{ color: C.muted }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: C.border }}>
        <Typography id="export-format-label" sx={sectionLabel}>
          Format
        </Typography>
        <RadioGroup
          aria-labelledby="export-format-label"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1, mb: 2 }}
        >
          {Object.entries(EXPORT_FORMATS).map(([key, f]) => (
            <Option
              key={key}
              name="export-format"
              value={key}
              selected={format === key}
              disabled={busy}
              icon={FORMAT_ICONS[key]}
              title={f.extension ? `${f.label} (.${f.extension})` : f.label}
              detail={f.description}
            />
          ))}
        </RadioGroup>

        <Typography id="export-scope-label" sx={sectionLabel}>
          Records
        </Typography>
        <RadioGroup aria-labelledby="export-scope-label" value={scope} onChange={(e) => setScope(e.target.value)} sx={{ display: "grid", gap: 1 }}>
          {scopes.map((s) => (
            <Option key={s.value} name="export-scope" value={s.value} selected={scope === s.value} disabled={busy} title={s.title} detail={s.detail} />
          ))}
        </RadioGroup>

        <Typography sx={{ fontSize: 12, color: C.muted, mt: 1.5 }}>
          Excel and CSV include every report field. PDF and Print include the columns shown in the table.
        </Typography>

        <Stack spacing={1} sx={{ mt: 1.5 }} aria-live="polite">
          {count === 0 && <Alert severity="info">There are no records in the selected scope, so there is nothing to export.</Alert>}
          {pdfBlocked && (
            <Alert severity="warning">
              Some of these records contain Hindi or other Unicode text that the PDF generator cannot render. Choose Print and then “Save as PDF” to keep the text readable.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={close} disabled={busy} sx={{ textTransform: "none", fontWeight: 600, color: "#334155" }}>
          Cancel
        </Button>
        <Button
          onClick={submit}
          disabled={!canExport}
          variant="contained"
          disableElevation
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", bgcolor: C.primary, "&:hover": { bgcolor: C.primaryHover }, ...focusRing }}
        >
          {busy ? "Generating…" : format === "print" ? `Print ${count} records` : `Export ${count} records`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
